import { t } from '@lingui/macro';
import { PermissionRuleCard } from 'apps/TenantApp/components/PermissionRuleCard';
import React from 'react';
import { graphql, useFragment } from 'utils/graphClient';
import { notEmpty } from 'utils/NotEmptyFilter';
import { EventPermissionRules_event$key } from './__generated__/EventPermissionRules_event.graphql';

const fragment = graphql`
  fragment EventPermissionRules_event on Event {
    id
    canUpdate {
      value
    }
    ownershipSpace {
      id
    }
    permissionConfig {
      canCreateFolderInRoot {
        allowedRoles
        allowedMembers {
          edges {
            node {
              id
              name
              avatar {
                name
                url
              }
              ...UserHandle_member
            }
          }
        }
      }
      canCreateItemInRoot {
        allowedRoles
        allowedMembers {
          edges {
            node {
              id
              name
              avatar {
                name
                url
              }
              ...UserHandle_member
            }
          }
        }
      }
    }
  }
`;

interface EventPermissionRulesProps {
  event: EventPermissionRules_event$key;
}

export const EventPermissionRules = ({ event }: EventPermissionRulesProps) => {
  const {
    id,
    canUpdate: { value: canUpdateRules },
    permissionConfig,
    ownershipSpace: { id: spaceId },
  } = useFragment(fragment, event);

  const folderCreateRuleConfig = {
    allowAllMembers:
      permissionConfig.canCreateFolderInRoot.allowedRoles.includes(
        'SPACE_MEMBERS',
      ),
    allowedMembers: permissionConfig.canCreateFolderInRoot.allowedMembers.edges
      .map(({ node }) => node)
      .filter(notEmpty),
  };
  const contentCreateRuleConfig = {
    allowAllMembers:
      permissionConfig.canCreateItemInRoot.allowedRoles.includes(
        'SPACE_MEMBERS',
      ),
    allowedMembers: permissionConfig.canCreateItemInRoot.allowedMembers.edges
      .map(({ node }) => node)
      .filter(notEmpty),
  };
  return (
    <>
      <PermissionRuleCard
        userFriendlyRuleName={t`Create top-level folders`}
        ruleDescription={t`Control who can create new folders at the top level of the event's files.`}
        ruleConfig={folderCreateRuleConfig}
        canUpdate={canUpdateRules}
        spaceId={spaceId}
        graphRuleName="canCreateFolderInRoot"
        nodeId={id}
      />
      <PermissionRuleCard
        userFriendlyRuleName={t`Create top-level content`}
        ruleDescription={t`Control who can create new non-folder content at the top level of the event's files.`}
        ruleConfig={contentCreateRuleConfig}
        canUpdate={canUpdateRules}
        spaceId={spaceId}
        graphRuleName="canCreateItemInRoot"
        nodeId={id}
      />
    </>
  );
};
